import Clipboard from "@react-native-clipboard/clipboard";
import {
    DataTable,
    IconButton,
    Loading,
    PageTitle,
    StyleFunction,
    Surface,
    Text,
    useModal,
    usePagination,
    useThemedStyle,
    useToast,
} from "@venuepos/react-common";
import { GQEconomicSyncJobsQuery, useEconomicSyncJobsQuery } from "graphql-sdk";
import { formatDateTime } from "lib";
import { AvailableLocale, useTranslation } from "locales";
import React, { useCallback } from "react";
import { useAdminSession } from "../../session";
import { AdminContainer } from "../container";
import { LastError } from "./last-error";

export function EconomicSyncJobsListScreen() {
    const styles = useThemedStyle(styleFunc);
    const [t] = useTranslation();
    const toast = useToast();
    const [{ locale }] = useAdminSession(["locale"]);
    const { render } = useModal();

    const {
        page,
        pageSize,
        sortBy,
        sortDirection,
        onSortChange,
        onPageChange,
        onPageSizeChange,
    } = usePagination({
        initialSortBy: "createdAt",
        initialSortDirection: "DESC",
    });

    const { data, loading } = useEconomicSyncJobsQuery({
        variables: {
            pagination: {
                page,
                pageSize,
                sort: sortBy,
                sortDirection: sortDirection,
            },
        },
        fetchPolicy: "no-cache",
    });

    const copyOnClick = useCallback(
        (text: string) => {
            Clipboard.setString(text);
            toast.info(t("backoffice.info.text_copied", "Text copied"));
        },
        [t, toast]
    );

    const handleLastErrorPress = useCallback(
        async (
            item: GQEconomicSyncJobsQuery["economicSyncJobs"]["data"][0]
        ) => {
            await render(onClose => (
                <LastError onClose={onClose} output={item.lastError} />
            ));
        },
        [render]
    );

    const renderJobRow = useCallback(
        (
            item: GQEconomicSyncJobsQuery["economicSyncJobs"]["data"][0],
            rowIndex
        ) => (
            <DataTable.Row
                style={rowIndex % 2 === 1 ? styles.oddRow : undefined}
                key={`${item.id}-${item.runAt}`}
            >
                <DataTable.Cell numeric style={styles.mediumColumn}>
                    <Text numberOfLines={1}>{item.id}</Text>
                    <IconButton
                        size="small"
                        onPress={() => copyOnClick(item.id.toString())}
                        name="copy"
                        color="black"
                    />
                </DataTable.Cell>
                <DataTable.Cell style={styles.veryLargeColumn}>
                    <Text numberOfLines={1}>{item.shiftId}</Text>
                    {item.shiftId && (
                        <IconButton
                            size="small"
                            onPress={() => copyOnClick(item.shiftId)}
                            name="copy"
                            color="black"
                        />
                    )}
                </DataTable.Cell>
                <DataTable.Cell style={styles.largeColumn}>
                    {item.shift?.merchant.name}
                </DataTable.Cell>
                <DataTable.Cell numeric style={styles.smallColumn}>
                    {item.attempts}
                </DataTable.Cell>
                <DataTable.Cell>
                    {item.lastError && (
                        <>
                            <Text numberOfLines={1}>{item.lastError}</Text>
                            <IconButton
                                name="eye"
                                style={styles.icon}
                                onPress={() => handleLastErrorPress(item)}
                            />
                        </>
                    )}
                </DataTable.Cell>
                <DataTable.Cell numeric style={styles.largeColumn}>
                    {item.runAt &&
                        formatDateTime(item.runAt, locale as AvailableLocale)}
                </DataTable.Cell>
                <DataTable.Cell numeric style={styles.largeColumn}>
                    {item.createdAt &&
                        formatDateTime(
                            item.createdAt,
                            locale as AvailableLocale
                        )}
                </DataTable.Cell>
            </DataTable.Row>
        ),
        [
            styles.oddRow,
            styles.mediumColumn,
            styles.veryLargeColumn,
            styles.largeColumn,
            styles.smallColumn,
            styles.icon,
            locale,
            copyOnClick,
            handleLastErrorPress,
        ]
    );

    return (
        <AdminContainer>
            <PageTitle>
                {t(
                    "backoffice.economic_sync.economic_sync_jobs_list",
                    "E-conomic sync jobs"
                )}
            </PageTitle>

            <Surface>
                {data && data.economicSyncJobs.data.length === 0 ? (
                    <Text>{t("common.no_data", "No data")}</Text>
                ) : (
                    <DataTable>
                        <DataTable.Header>
                            <DataTable.Title
                                numeric
                                style={styles.mediumColumn}
                                sortDirection={
                                    sortBy === "id" ? sortDirection : undefined
                                }
                                onPress={() => onSortChange("id")}
                            >
                                {t("backoffice.economic_sync.id", "ID")}
                            </DataTable.Title>
                            <DataTable.Title
                                style={styles.veryLargeColumn}
                                sortDirection={
                                    sortBy === "shiftId"
                                        ? sortDirection
                                        : undefined
                                }
                                onPress={() => onSortChange("shiftId")}
                            >
                                {t(
                                    "backoffice.economic_sync.shift_id",
                                    "Shift"
                                )}
                            </DataTable.Title>
                            <DataTable.Title style={styles.largeColumn}>
                                {t(
                                    "backoffice.economic_sync.merchant",
                                    "Merchant"
                                )}
                            </DataTable.Title>
                            <DataTable.Title
                                style={styles.smallColumn}
                                numeric
                                sortDirection={
                                    sortBy === "attempts"
                                        ? sortDirection
                                        : undefined
                                }
                                onPress={() => onSortChange("attempts")}
                            >
                                {t(
                                    "backoffice.economic_sync.attempts",
                                    "Attempts"
                                )}
                            </DataTable.Title>
                            <DataTable.Title
                                sortDirection={
                                    sortBy === "lastError"
                                        ? sortDirection
                                        : undefined
                                }
                                onPress={() => onSortChange("lastError")}
                            >
                                {t(
                                    "backoffice.economic_sync.last_error",
                                    "Last error"
                                )}
                            </DataTable.Title>
                            <DataTable.Title
                                style={styles.largeColumn}
                                numeric
                                sortDirection={
                                    sortBy === "runAt"
                                        ? sortDirection
                                        : undefined
                                }
                                onPress={() => onSortChange("runAt")}
                            >
                                {t("backoffice.economic_sync.run_at", "Run at")}
                            </DataTable.Title>
                            <DataTable.Title
                                style={styles.largeColumn}
                                numeric
                                sortDirection={
                                    sortBy === "createdAt"
                                        ? sortDirection
                                        : undefined
                                }
                                onPress={() => onSortChange("createdAt")}
                            >
                                {t("common.created_at", "Created at")}
                            </DataTable.Title>
                        </DataTable.Header>
                        {loading ? (
                            <Loading />
                        ) : (
                            data?.economicSyncJobs.data.map(renderJobRow)
                        )}
                        <DataTable.Pagination
                            onPageChange={onPageChange}
                            pageSize={pageSize}
                            onSizeChange={onPageSizeChange}
                            page={page}
                            numberOfPages={
                                data?.economicSyncJobs.pagination.pages
                            }
                            itemCount={
                                data?.economicSyncJobs.pagination.resultCount
                            }
                        />
                    </DataTable>
                )}
            </Surface>
        </AdminContainer>
    );
}

const styleFunc: StyleFunction = theme => ({
    oddRow: {
        backgroundColor: theme.colors.grey50,
    },
    icon: {
        color: theme.colors.secondary,
        paddingHorizontal: theme.spacingScale / 2,
    },
    smallColumn: {
        flexBasis: 80,
        flexGrow: 0,
        flexShrink: 0,
    },

    mediumColumn: {
        flexBasis: 130,
        flexGrow: 0,
        flexShrink: 0,
    },

    largeColumn: {
        flexBasis: 230,
        flexGrow: 0,
        flexShrink: 0,
    },
    veryLargeColumn: {
        flexBasis: 330,
        flexGrow: 0,
        flexShrink: 0,
    },
});
